import type { FormEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { ChildSelector } from '@/components/builder/ChildSelector';
import { MediaOptionsGroup } from '@/components/builder/MediaOptionsGroup';
import { RandomToggle } from '@/components/builder/RandomToggle';
import {
  StorydurationSelector,
  type StoryDuration,
} from '@/components/builder/StorydurationSelector';
import { SubmitButton } from '@/components/builder/SubmitButton';
import { ThemeInput } from '@/components/builder/ThemeInput';
import { VoiceSelector, type Voice } from '@/components/builder/VoiceSelector';
import { BuilderGate } from '@/components/builder/BuilderGate';
import { SeriesModeToggle, type BuilderMode } from '@/components/builder/series/SeriesModeToggle';
import { SeriesBuilderForm } from '@/components/builder/series/SeriesBuilderForm';
import { useSeriesEnabled } from '@/hooks/useSeriesEnabled';
import { useBuilderUrlPrefill } from './use-builder-url-prefill';
import { clearEntitlementCache } from '@/hooks/useEntitlement';
import { clearSubscriptionStateCache } from '@/hooks/useSubscriptionState';
import { useStoryWorlds } from '@/hooks/useStoryWorlds';
import { apiFetch, ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';

// Shape of POST /api/stories/jobs body. Source: src/story-jobs/dto/create-story-job.dto.ts.
interface CreateStoryJobRequest {
  worldId: string;
  childId: string;
  inputType: 'manual' | 'random';
  customTheme?: string;
  storyDuration: 'short' | 'standard' | 'long';
  narratorGender: 'male' | 'female';
  wantsAudio: boolean;
  wantsPdf: boolean;
}

interface CreateStoryJobResponse {
  jobId: string;
  status: string;
  totalSteps: number;
}

export function AABuilderRoute() {
  const S = STRINGS.builder.shared;
  const SAA = STRINGS.builder.aa;
  const navigate = useNavigate();
  const { getWorldIdBySlug } = useStoryWorlds();

  // Batch 10 Sub-phase E — `?theme=` URL pre-fill from IdeaTile
  // (Inspiration-Tab) navigation. Hook captures-on-mount + clears
  // the URL via replace so back-button doesn't re-fill on refresh.
  const { initialValue: initialTheme } = useBuilderUrlPrefill();
  const { enabled: seriesEnabled } = useSeriesEnabled();
  const [mode, setMode] = useState<BuilderMode>('single');
  const [theme, setTheme] = useState(initialTheme ?? '');
  const [isRandom, setIsRandom] = useState(false);
  const [childId, setChildId] = useState<string | null>(null);
  const [storyduration, setStoryduration] = useState<StoryDuration>('standard');
  const [pdf, setPdf] = useState(false);
  const [audio, setAudio] = useState(false);
  const [voice, setVoice] = useState<Voice>('male');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Hoisted from SeriesBuilderForm via onPlanningChange. Used to hide the
  // SeriesModeToggle while the plan is being generated — the toggle is
  // inoperable during that wait.
  const [seriesPlanning, setSeriesPlanning] = useState(false);

  const submitDisabled = (!theme.trim() && !isRandom) || !childId;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitDisabled || isSubmitting) return;

    const worldId = getWorldIdBySlug('aa');
    if (!worldId || !childId) {
      console.error('[AABuilder] missing worldId or childId', { worldId, childId });
      return;
    }

    const payload: CreateStoryJobRequest = {
      worldId,
      childId,
      inputType: isRandom ? 'random' : 'manual',
      storyDuration: storyduration,
      narratorGender: voice,
      wantsAudio: audio,
      wantsPdf: pdf,
    };
    if (!isRandom && theme.trim()) {
      payload.customTheme = theme.trim();
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const response = await apiFetch<CreateStoryJobResponse>('/api/stories/jobs', {
        method: 'POST',
        body: payload,
      });
      // B1 (Bug 2): clear-only (no eager refetch — deduction commits at pipeline
      // step 6, after this POST returns; refetching now would re-cache the
      // pre-deduction remaining). B2/B3 refresh after the deduction settles.
      clearEntitlementCache();
      clearSubscriptionStateCache();
      navigate(`/output/${response.jobId}`, {
        state: { world: 'aa', wantsAudio: audio, wantsPdf: pdf },
      });
      // Intentionally don't reset isSubmitting — we're navigating away.
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.envelope?.user_message ?? STRINGS.auth.errors.genericBackend);
      } else if (err instanceof NetworkError) {
        setSubmitError(STRINGS.auth.errors.networkError);
      } else {
        setSubmitError(STRINGS.auth.errors.genericBackend);
      }
      setIsSubmitting(false);
    }
  };

  const themePlaceholder = isRandom ? S.themePlaceholderRandom : S.themePlaceholderAA;

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div
        className="pointer-events-none absolute top-4 left-4 h-[120px] md:h-[180px] desktop:h-[240px]"
        aria-hidden="true"
      >
        <img src="/worlds/aa/clouds.svg" alt="" className="block h-full w-auto" />
        <img
          src="/worlds/aa/airplane.svg"
          alt=""
          className="absolute top-[15%] left-[35%] h-[55%] w-auto"
        />
      </div>
      <div className="relative mx-auto max-w-[560px] px-6 pb-12 pt-[152px] md:pt-[212px] desktop:pt-[272px]">
        <h1 className="text-h3 mb-6 font-semibold text-dream-navy">{SAA.pageTitle}</h1>

        <BuilderGate>
          {seriesEnabled && !seriesPlanning && (
            <div className="mb-5">
              <SeriesModeToggle value={mode} onChange={setMode} />
            </div>
          )}
          {mode === 'series' ? (
            <SeriesBuilderForm worldSlug="aa" onPlanningChange={setSeriesPlanning} />
          ) : (
            <form onSubmit={handleSubmit}>
              <fieldset disabled={isSubmitting} className="flex flex-col gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="aa-theme" className="text-sm text-dream-navy">
                    {S.themaLabel}
                  </Label>
                  <ThemeInput
                    id="aa-theme"
                    value={theme}
                    onChange={setTheme}
                    placeholder={themePlaceholder}
                    disabled={isRandom}
                    maxLength={300}
                  />
                </div>

                <RandomToggle checked={isRandom} onCheckedChange={setIsRandom} />

                <ChildSelector
                  id="aa-child"
                  label={S.childSelectorLabel}
                  value={childId}
                  onChange={setChildId}
                />

                <div className="space-y-1.5">
                  <Label htmlFor="aa-story-duration" className="text-sm text-dream-navy">
                    {S.storydurationSelectorLabel}
                  </Label>
                  <StorydurationSelector
                    id="aa-story-duration"
                    value={storyduration}
                    onChange={setStoryduration}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-dream-navy">{S.optionsLabel}</Label>
                  <MediaOptionsGroup
                    pdfActive={pdf}
                    audioActive={audio}
                    onPdfChange={setPdf}
                    onAudioChange={setAudio}
                  />
                </div>

                {audio && <VoiceSelector value={voice} onChange={setVoice} />}

                {submitError && (
                  <div
                    role="alert"
                    className="rounded-md border border-[rgba(226,75,74,0.4)] bg-[rgba(226,75,74,0.15)] p-3 text-sm text-[color:var(--color-error)]"
                  >
                    {submitError}
                  </div>
                )}

                <SubmitButton disabled={submitDisabled} loading={isSubmitting} type="submit" />
              </fieldset>
            </form>
          )}
        </BuilderGate>
      </div>
      <div className="relative flex justify-end pr-4 pb-4 md:pr-6 md:pb-6">
        <img
          src="/worlds/aa/firefighter.svg"
          alt=""
          aria-hidden="true"
          className="block h-[120px] w-auto md:h-[180px] desktop:h-[240px]"
        />
      </div>
    </div>
  );
}
